import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Dices, LayoutDashboard, Building2, Gift, FileText, MessageSquare, 
  Image, Mail, Users, LogOut, Menu, X, Plus, Trash2, Check, Eye, 
  ChevronRight, TrendingUp, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sidebarLinks = [
  { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
  { name: "Casinos", path: "/admin/casinos", icon: Building2 },
  { name: "Bonuses", path: "/admin/bonuses", icon: Gift },
  { name: "Blogs", path: "/admin/blogs", icon: FileText },
  { name: "Complaints", path: "/admin/complaints", icon: MessageSquare },
  { name: "Reviews", path: "/admin/reviews", icon: Users },
  { name: "Contacts", path: "/admin/contacts", icon: Mail },
  { name: "Banners", path: "/admin/banners", icon: Image },
];

function useAuth() {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
    }
  }, [token, navigate]);

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminName");
    navigate("/admin/login");
  };

  return { token, logout };
}

function AdminSidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const { logout } = useAuth();
  const adminName = localStorage.getItem("adminName") || "Admin";

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-white/10 transform transition-transform lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl gradient-neon flex items-center justify-center">
                  <Dices className="w-6 h-6 text-white" />
                </div>
                <span className="text-lg font-bold text-white font-['Montserrat']">
                  Admin
                </span>
              </Link>
              <button 
                className="lg:hidden text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? "bg-primary/20 text-primary" 
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{adminName}</p>
                <p className="text-white/40 text-sm">Administrator</p>
              </div>
              <button 
                onClick={logout}
                className="p-2 text-white/40 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function DashboardOverview() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStats();
  }, [token]);

  if (loading) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="h-24 rounded-xl bg-card animate-pulse" />
      ))}
    </div>;
  }

  const statCards = [
    { label: "Total Casinos", value: stats?.casinos || 0, icon: Building2, color: "text-primary" },
    { label: "Total Reviews", value: stats?.reviews || 0, icon: Users, color: "text-green-400" },
    { label: "Pending Reviews", value: stats?.pending_reviews || 0, icon: AlertCircle, color: "text-orange-400" },
    { label: "Complaints", value: stats?.complaints || 0, icon: MessageSquare, color: "text-red-400" },
    { label: "Pending Complaints", value: stats?.pending_complaints || 0, icon: AlertCircle, color: "text-yellow-400" },
    { label: "Blog Articles", value: stats?.blogs || 0, icon: FileText, color: "text-blue-400" },
    { label: "Subscribers", value: stats?.subscribers || 0, icon: Mail, color: "text-purple-400" },
    { label: "Contact Messages", value: stats?.contacts || 0, icon: Mail, color: "text-secondary" },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className="glass rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-white/50 text-sm">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-white mb-4 font-['Montserrat']">Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/admin/casinos" className="glass rounded-xl p-4 hover:bg-white/5 transition-colors group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-primary" />
                <span className="text-white">Manage Casinos</span>
              </div>
              <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
            </div>
          </Link>
          <Link to="/admin/complaints" className="glass rounded-xl p-4 hover:bg-white/5 transition-colors group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-orange-400" />
                <span className="text-white">View Complaints</span>
                {stats?.pending_complaints > 0 && (
                  <Badge className="bg-orange-500/20 text-orange-400">{stats.pending_complaints}</Badge>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
            </div>
          </Link>
          <Link to="/admin/reviews" className="glass rounded-xl p-4 hover:bg-white/5 transition-colors group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-green-400" />
                <span className="text-white">Moderate Reviews</span>
                {stats?.pending_reviews > 0 && (
                  <Badge className="bg-green-500/20 text-green-400">{stats.pending_reviews}</Badge>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ComplaintsManager() {
  const { token } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchComplaints = async () => {
    try {
      const response = await axios.get(`${API}/complaints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComplaints(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchComplaints();
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/complaints/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    reviewing: "bg-blue-500/20 text-blue-400",
    contacted: "bg-purple-500/20 text-purple-400",
    resolved: "bg-green-500/20 text-green-400",
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Complaints</h1>
      
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-card animate-pulse" />
          ))}
        </div>
      ) : complaints.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/60">No complaints found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {complaints.map((complaint) => (
            <div key={complaint.id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-bold">{complaint.title}</h3>
                  <p className="text-white/50 text-sm">
                    {complaint.name} • {complaint.casino_name}
                  </p>
                </div>
                <Badge className={statusColors[complaint.status]}>
                  {complaint.status}
                </Badge>
              </div>
              <p className="text-white/70 text-sm line-clamp-2 mb-4">{complaint.details}</p>
              <div className="flex items-center gap-2">
                <Select
                  value={complaint.status}
                  onValueChange={(value) => updateStatus(complaint.id, value)}
                >
                  <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewsManager() {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews?approved_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchReviews();
  }, [token]);

  const approveReview = async (id) => {
    try {
      await axios.put(`${API}/reviews/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Review approved");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to approve review");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Reviews</h1>
      
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-card animate-pulse" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/60">No reviews found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white font-bold">{review.title}</h3>
                  <p className="text-white/50 text-sm">{review.user_name} • Rating: {review.rating}/5</p>
                </div>
                <Badge className={review.is_approved ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                  {review.is_approved ? "Approved" : "Pending"}
                </Badge>
              </div>
              <p className="text-white/70 text-sm line-clamp-2 mb-4">{review.content}</p>
              <div className="flex items-center gap-2">
                {!review.is_approved && (
                  <Button size="sm" onClick={() => approveReview(review.id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => deleteReview(review.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactsManager() {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get(`${API}/contacts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContacts(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchContacts();
  }, [token]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Contact Messages</h1>
      
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-card animate-pulse" />
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/60">No messages found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white font-bold">{contact.subject}</h3>
                  <p className="text-white/50 text-sm">{contact.name} • {contact.email}</p>
                </div>
              </div>
              <p className="text-white/70 text-sm">{contact.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimplePlaceholder({ title, icon: Icon }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">{title}</h1>
      <div className="glass rounded-xl p-8 text-center">
        <Icon className="w-12 h-12 text-white/20 mx-auto mb-4" />
        <p className="text-white/60">Management interface coming soon</p>
        <p className="text-white/40 text-sm mt-2">Use the API directly to manage {title.toLowerCase()}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { token } = useAuth();

  if (!token) return null;

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden glass border-b border-white/10 p-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="text-white">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-neon flex items-center justify-center">
                <Dices className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white">Admin</span>
            </div>
            <div className="w-6" />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="casinos" element={<SimplePlaceholder title="Casinos" icon={Building2} />} />
            <Route path="bonuses" element={<SimplePlaceholder title="Bonuses" icon={Gift} />} />
            <Route path="blogs" element={<SimplePlaceholder title="Blogs" icon={FileText} />} />
            <Route path="complaints" element={<ComplaintsManager />} />
            <Route path="reviews" element={<ReviewsManager />} />
            <Route path="contacts" element={<ContactsManager />} />
            <Route path="banners" element={<SimplePlaceholder title="Banners" icon={Image} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
